import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from './translations';

const LanguageContext = createContext();

// A sandboxed iframe without allow-same-origin gets an opaque origin, and any
// localStorage access there throws SecurityError. app.0v.network embeds this
// page with exactly that sandbox, so an unguarded read in the useState
// initializer below would throw during first render and blank the whole app.
// Falling back to a non-persistent session keeps the page working when framed.
const storage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* storage unavailable — the choice just won't survive a reload */
    }
  },
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = storage.get('language');
    if (saved && translations[saved]) return saved;

    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang.startsWith('zh')) {
      if (browserLang === 'zh-TW' || browserLang === 'zh-HK') return 'zh-TW';
      return 'zh-CN';
    }
    if (browserLang.startsWith('ja')) return 'ja';
    return 'en';
  });

  useEffect(() => {
    storage.set('language', language);
    // Drives lang-specific type sizing (CJK needs smaller display sizes) and a11y.
    document.documentElement.lang = language;
  }, [language]);

  const t = (key, params = {}) => {
    let text = translations[language]?.[key] || translations['zh-CN']?.[key] || key;

    Object.keys(params).forEach(param => {
      text = text.replace(`{${param}}`, params[param]);
    });

    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
